import { ContentBlock, ContentState, DraftDecorator } from "draft-js";
import React, { JSX, useState } from "react";
import styled from "styled-components";
import { Z } from "@ui/styles/zIndex";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { DeleteIcon } from "@ui/components/icons";
import { LinkExternal } from "@ui/components/linkExternal";

export const linkStrategy = (
	contentBlock: ContentBlock,
	callback: (start: number, end: number) => void,
	contentState: ContentState
): void => {
	contentBlock.findEntityRanges((character) => {
		const entityKey = character.getEntity();
		return entityKey !== null && contentState.getEntity(entityKey).getType() === "LINK";
	}, callback);
};

export const RichLink = (props: {
	contentState: ContentState;
	entityKey: string;
	children: React.ReactNode;
	editable?: boolean;
	isMention?: boolean;
}): JSX.Element => {
	const { contentState, entityKey, editable, isMention } = props;
	const [tooltipVisible, setTooltipVisible] = useState(false);
	const { url } = contentState.getEntity(entityKey).getData();
	const strUrl = url as string;
	const externalLink = isMention
		? strUrl
		: strUrl.startsWith("https://") || strUrl.startsWith("http://")
			? strUrl
			: "https://" + strUrl;
	const openLink = (): Window | null => window.open(externalLink, "_blank");

	return (
		<LinkWrapper>
			<StyledLink
				validLink={strUrl.length > 0}
				to={externalLink}
				onMouseEnter={() => setTooltipVisible(true)}
				onMouseLeave={() => setTooltipVisible(false)}
			>
				{props.children}
			</StyledLink>

			{tooltipVisible && !isMention && strUrl.length > 0 && (
				<TooltipWrapper onMouseEnter={() => setTooltipVisible(true)} onMouseLeave={() => setTooltipVisible(false)}>
					<LinkTooltip onClick={() => !editable && openLink()}>
						<UrlLink onClick={() => editable && openLink()}>{externalLink}</UrlLink>
						{editable && (
							<DeleteButton
								onClick={(e) => {
									e.stopPropagation();
									contentState.replaceEntityData(entityKey, { url: "" });
									setTooltipVisible(false);
								}}
							>
								<DeleteIcon />
							</DeleteButton>
						)}
					</LinkTooltip>
				</TooltipWrapper>
			)}
		</LinkWrapper>
	);
};

export const createLinkDecorators = ({ editable }: { editable: boolean }): DraftDecorator[] => {
	return [
		{
			strategy: linkStrategy,
			component: editable ? EditableLink : RichLink,
		},
	];
};

export const EditableLink = (props: {
	contentState: ContentState;
	entityKey: string;
	children: React.ReactNode;
}): JSX.Element => {
	return <RichLink {...props} editable />;
};

const LinkWrapper = styled.div`
	position: relative;
	display: inline-block;
	width: fit-content;
`;

const StyledLink = styled(LinkExternal)<{ validLink: boolean }>`
	color: ${({ theme, validLink }) => (validLink ? theme.colors.link : theme.colors.black)};
`;

const TooltipWrapper = styled.div`
	position: absolute;
	transform: translateX(-50%);
	padding: 15px;
	left: 50%;
	top: 15px;
	z-index: ${Z.over};
	width: max-content;
	max-width: 350px;
`;

const LinkTooltip = styled(RoundedBlock)`
	display: flex;
	flex-direction: row;
	align-items: center;
	padding: 10px 5px 10px 10px;
	user-select: none;
	cursor: pointer;

	&::after {
		content: "";
		position: absolute;
		background-color: ${({ theme }) => theme.colors.backgroundPrimary};
		border-top-left-radius: 4px;
		border-left: 1px solid ${({ theme }) => theme.colors.lightergray};
		border-top: 1px solid ${({ theme }) => theme.colors.lightergray};
		width: 14px;
		height: 14px;
		left: 50%;
		top: 0px;
		transform: translate(-50%, 50%) rotate(45deg);
	}
`;

const UrlLink = styled.span`
	margin-right: 5px;
	font-size: 0.8125rem;
	font-style: italic;
	&:hover {
		color: ${({ theme }) => theme.colors.link};
	}
`;

const DeleteButton = styled.div`
	cursor: pointer;

	&:hover svg {
		transform: rotate(90deg);
	}
	svg {
		transition: transform 0.2s ease-in-out;
	}
`;
